import React, {useEffect, useState, useMemo} from "react";
import ProductCard from "../ProductCard/ProductCard";
import {mapArticleModelToProductData} from "../Cart/cart.dto";
import {CiViewTable} from "react-icons/ci";
import {BiSolidGridAlt} from "react-icons/bi";
import {ProductsListResponse} from "@/api/ArticlesAPI";
import {toast} from "../ui/use-toast";
import {ArticleModel} from "@/api/Models";
import LoadingElement from "../Loading/LoadingElement";
import ProductTable from "../ProductCard/ProductTable";
import noImageAvailable from "@/assets/images.png";
import {useLanguage} from "../Navbar/LanguageContext";
import {
    TableBody,
    TableCell,
    TableRow,
} from "@/components/ui/table"
import ApiHttpClient from "@/HttpClient";
import {debounce} from "lodash";
import {useCheckUserUM} from "../utils/useCheckUserUM";
import {getDecodedTokenDetails} from "../Token/TokenService";

const SearchInput: React.FC<any> = ({setSearchArticle, setIsNewSearch}) => {
    const debouncedSearch = useMemo(() =>
        debounce((value: string) => {
            if (value.length > 2) {
                setSearchArticle(value.toLowerCase());
                setIsNewSearch(true);
            } else {
                setSearchArticle("");
            }
        }, 1000), [setSearchArticle, setIsNewSearch]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };
    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            onChange={handleChange}
            placeholder="Cauta numele unui produs..."
        />
    );
};

function ProductList() {
    const [isMultiple, setIsMultiply] = useState(false);
    const [isTabular, setIsTabular] = useState(true);
    const [articlesStock, setArticlesStock] = useState(false);
    const [articlesTransit, setArticlesTransit] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchArticle, setSearchArticle] = useState("");
    const [data, setData] = useState<ProductsListResponse>();
    const [isLoading, setIsLoading] = useState(false);
    const [isNewSearch, setIsNewSearch] = useState(false);
    const {language} = useLanguage();
    const pageSize = 55;
    const [userUM, setUserUM] = useState<boolean | null>(null);
    const [atacFlowUser, setAtacFlowUser] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    useCheckUserUM((value: boolean) => setUserUM(value));

    useEffect(() => {
        if (userUM === null) return;

        if (userUM) {
            setIsMultiply(true);
            setIsTabular(false);
        } else {
            setIsMultiply(false);
            setIsTabular(true);
        }
    }, [userUM]);


    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    const userAtacFlow = tokenDetails.atacFlow;
                    setAtacFlowUser(userAtacFlow);
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    }
    
    // const showHorizontally = () => {
    //     setIsHorizontally((current) => !current);
    //     setIsMultiply(false);
    //     setIsTabular(false);
    // };

    const showMultiply = () => {
        setIsMultiply(true);
        //setIsHorizontally(false);
        setIsTabular(false);
    };

    const showTable = () => {
        setIsTabular(true);
        //setIsHorizontally(false);
        setIsMultiply(false);
    };

    const handlePageChange = (selectedPage: number) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };

    useEffect(() => {
        if (userUM !== null) {
            const controller = new AbortController();

            const updateProducts = async () => {
                try {
                    setIsLoading(true);
                    let response;
                    const requestBody: any = {
                        advancedSearch: {
                            fields: [
                                "articleId",
                                "longName",
                                "longNameWeb",
                                "longNameEsl",
                                "longNameFr",
                            ],
                            keyword: searchArticle
                        },
                        pageNumber: currentPage + 1,
                        pageSize: pageSize
                    };

                    const requestBodySimple: any = {
                        keyword: "",
                        pageNumber: currentPage + 1,
                        pageSize: pageSize
                    };

                    if (articlesStock || articlesTransit) {
                        requestBody.advancedFilter = {
                            field: "flowType",
                            operator: "eq",
                            value: articlesTransit ? "TR" : "ST"
                        };

                        requestBodySimple.advancedFilter = {
                            field: "flowType",
                            operator: "eq",
                            value: articlesTransit ? "TR" : "ST"
                        };
                    }

                    if (searchArticle) {
                        if (isNewSearch) setCurrentPage(0);

                        setIsLoading(true);

                        response = await
                            ApiHttpClient().post<ProductsListResponse>(`/articleAvailableForWorkingPoint/search`, requestBody, {
                                signal: controller.signal
                            });
                    } else {
                        response = await ApiHttpClient().post<ProductsListResponse>(`/articleAvailableForWorkingPoint/search`, requestBodySimple, {
                            signal: controller.signal
                        });
                    }
                    if (!response?.data) {
                        toast({description: "A aparut o eroare pentru afisarea articolelor"});
                        return <></>;
                    }
                    setIsLoading(false);
                    setData(response.data);
                    setIsNewSearch(false);
                } catch (error: any) {
                    if (error.name === 'AbortError') {
                        // Cererea a fost anulată — nu e eroare reală
                        return;
                    }
                    console.error(error);
                    toast({description: "A aparut o eroare la incarcarea datelor"});
                } finally {
                    setIsLoading(false);
                }
            }

            updateProducts();

            return () => {
                controller.abort();
            };
        }
    }, [userUM, searchArticle, currentPage, pageSize, isNewSearch, articlesTransit, articlesStock]);
    
    const handleShowArticlesTransit = () => {
        setArticlesTransit(true);
        setArticlesStock(false);
        setCurrentPage(0);
    }

    const handleShowArticlesStock = () => {
        setArticlesStock(true);
        setArticlesTransit(false);
        setCurrentPage(0);
    }

    const handleRemoveFilters = () => {
        setArticlesStock(false);
        setArticlesTransit(false);
        setCurrentPage(0);
    }

    return (
        <>
            <section>
                <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 sm:py-2 lg:px-8">
                    <header>
                        {articlesStock ? <h2 className="text-xl font-bold text-gray-900 my-3 sm:text-3xl">
                            Toate produsele cu stoc
                        </h2> : (articlesTransit ? <h2 className="text-xl font-bold text-gray-900 my-3 sm:text-3xl">
                            Toate produsele cu livrare programata
                        </h2> : <h2 className="text-xl font-bold text-gray-900 my-3 sm:text-3xl">
                            Toate produsele
                        </h2>)}
                    </header>

                    <SearchInput setSearchArticle={setSearchArticle} setIsNewSearch={setIsNewSearch}/>

                    {!userUM && !atacFlowUser && <div className="mt-1 sm:flex sm:items-center sm:justify-between">
                        <div className="mt-6 sm:flex sm:items-center sm:flex-row space-x-3 mb-4">
                            <button
                                className="inline-flex items-center justify-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-emerald-700"
                                onClick={handleShowArticlesStock}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="-ms-1 me-1.5 size-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>

                                <p className="text-sm whitespace-nowrap">Produse cu stoc</p>
                            </button>

                            <button
                                className="inline-flex items-center justify-center rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-700"
                                onClick={handleShowArticlesTransit}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="-ms-1 me-1.5 size-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8.25 9.75h4.875a2.625 2.625 0 010 5.25H12M8.25 9.75L10.5 7.5M8.25 9.75L10.5 12m9-7.243V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z"
                                    />
                                </svg>

                                <p className="text-sm whitespace-nowrap">Livrare programata</p>
                            </button>
                        </div>

                        <button
                            className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                            onClick={handleRemoveFilters}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-3"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>

                            <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                        </button>
                    </div>}
                    
                    <div className="flex gap-4 mt-2">
                        <p className="flex justify-center mt-2">Afisare: </p>
                        <span className="inline-flex overflow-hidden rounded-md border bg-white shadow-sm">
                          {/*<button*/}
                          {/*    className="inline-block border-e p-3 text-gray-700 hover:bg-gray-50 focus:relative"*/}
                          {/*    title="Afisare orizontala"*/}
                          {/*    onClick={showHorizontally}*/}
                          {/*    disabled*/}
                          {/*    style={{*/}
                          {/*        backgroundColor: isHorizontally ? "#D3D3D3" : "#D3D3D3",*/}
                          {/*    }}*/}
                          {/*>*/}
                          {/*  <MdTableRows/>*/}
                          {/*</button>*/}
            
                          <button
                              className="inline-block border-e p-3 text-gray-700 hover:bg-gray-50 focus:relative"
                              title="Afisare multipla"
                              onClick={showMultiply}
                              style={{backgroundColor: isMultiple ? "white" : "#D3D3D3"}}
                          >
                            <BiSolidGridAlt/>
                          </button>

                          <button
                              className="inline-block border-e p-3 text-gray-700 hover:bg-gray-50 focus:relative"
                              title="Afisare tabelara"
                              onClick={showTable}
                              style={{
                                  backgroundColor: isTabular ? "white" : "#D3D3D3",
                              }}
                          >
                            <CiViewTable/>
                          </button>
                        </span>
                    </div>
                </div>
            </section>

            {isLoading && <LoadingElement/>}
            {!isLoading && data?.totalPages === 0 && <p>Nu au fost gasite rezultate.</p>}
            {!isLoading && data?.items && data?.totalPages !== 0 && <>
                {isMultiple && (
                    <section>
                        <div className="mx-auto mt-1 max-w-screen-2xl px-0 py-0 sm:px-6 sm:py-2 lg:px-8">
                            <ul className="mt-4 grid gap-1 sm:grid-cols-5 lg:grid-cols-5">
                                {data.items.map((article: ArticleModel) => {
                                    let product = mapArticleModelToProductData(article);
                                    return (
                                        <li key={product.id}>
                                            <div className="p-1 text-center">
                                                <ProductCard productData={product} autoPlayCarousel={false}/>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </section>
                )}

                {isTabular && (
                    <section>
                        <div className="max-w-[1320px] m-auto px-4 mt-1 py-0 sm:px-6 sm:py-2 lg:px-8">
                            <div className="relative overflow-auto" style={{maxHeight: '780px'}}>
                                <table className="w-full border-collapse">
                                    <thead>
                                    <tr className="bg-white border-slate-200">
                                        <th className="sticky top-0 bg-white z-10 text-left p-2 text-gray-500 text-sm">Cod
                                            articol
                                        </th>
                                        <th className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">Imagine</th>
                                        <th className="top-0 left-0 sticky bg-white z-20 text-gray-500 text-sm text-center whitespace-normal">Denumire <br/> articol
                                        </th>
                                        <th className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">UM</th>
                                        <th className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">Unitatea
                                            de vanzare
                                        </th>
                                        {!atacFlowUser && <th
                                            className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">
                                            Pret UM cu <span className="text-blue-900">TVA inclus</span>
                                        </th>}
                                        <th
                                            className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">
                                            Pret pe unitate de vanzare <span className="text-blue-900">fara TVA</span>
                                        </th>
                                        <th
                                            className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">
                                            Pret pe unitate de vanzare cu <span
                                            className="text-blue-900">TVA inclus</span>
                                        </th>
                                        {/* <th className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">Taxe si Garantii</th> */}
                                        <th className="sticky top-0 bg-white z-10 text-center p-2 text-gray-500 text-sm">
                                            Unitati de vanzare comandate / <br/> Total UM comandat
                                        </th>
                                    </tr>
                                    </thead>
                                    <TableBody>
                                        {data.items?.length > 0 && data.items.map((article: ArticleModel, index: number) => {
                                            let product = mapArticleModelToProductData(article);
                                            return (
                                                <TableRow key={product.id}
                                                          className={`${index % 2 === 0 ? "bg-slate-150" : "bg-slate-100"} [&>td]:py-1 [&>td]:px-2`}>
                                                    <TableCell>
                                                        {!userUM && product.isTransit ? <div className="flex flex-row">
                                                            <div
                                                                className="group relative m-2 flex justify-left">
                                                                <div
                                                                    className="h-2.5 w-2.5 rounded-full bg-yellow-500 me-2"></div>
                                                                <span
                                                                    className="absolute top-4 scale-0 z-50 rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100">✨ Livrare programata</span>
                                                            </div>

                                                            {product.id}
                                                        </div> : <div>{product.id}</div>}
                                                    </TableCell>
                                                    <TableCell>
                                                        {product.articleImage?.mainLink &&
                                                            <img src={product.articleImage?.mainLink} alt={"test"}
                                                                 className="size-14"/>
                                                        }
                                                        {!product.articleImage?.mainLink &&
                                                            <img src={noImageAvailable} alt={"test"}
                                                                 className="size-14"/>
                                                        }
                                                    </TableCell>
                                                    {userUM ?
                                                        <TableCell
                                                            className={index % 2 === 0 ? "bg-white sticky left-0 z-10 font-medium w-[150px]" : "bg-slate-100 sticky left-0 z-10 font-medium w-[100px]"}>
                                                            {language === "romana" && <a href={"/product/" + product.id}
                                                                                         className="group relative block overflow-hidden text-left"
                                                            >{product?.displayName}</a>}
                                                            {language === "franceza" && product?.longNameFR === null &&
                                                                <a href={"/product/" + product.id}
                                                                   className="group relative block overflow-hidden text-left"
                                                                >{product?.displayName}</a>}
                                                            {language === "franceza" && product?.longNameFR !== null &&
                                                                <a href={"/product/" + product.id}
                                                                   className="group relative block overflow-hidden text-left"
                                                                >{product?.longNameFR}</a>}
                                                        </TableCell> : <TableCell
                                                            className={index % 2 === 0 ? "bg-white sticky left-0 z-10 font-medium w-[150px]" : "bg-slate-100 sticky left-0 z-10 font-medium w-[100px]"}>
                                                            <a href={"/product/" + product.id}
                                                               className="group relative block overflow-hidden text-left text-wrap"
                                                            >{product.displayName}</a>
                                                        </TableCell>}
                                                    <TableCell>{product.salesUnit === 'A' &&
                                                        <p>Bucata</p>} {product.salesUnit === 'K' &&
                                                        <p>Kilogram</p>} {product.salesUnit === 'M' &&
                                                        <p>Metru</p>}</TableCell>
                                                    <TableCell>{product.multiplier}</TableCell>
                                                    {!atacFlowUser &&
                                                        <TableCell>{product.priceWithVAT1 && product.priceWithVAT1.toFixed(2)} RON </TableCell>}
                                                    {!atacFlowUser &&
                                                        <TableCell>{product.unitPrice1 && product.unitPrice1.toFixed(2)} RON</TableCell>}
                                                    {!atacFlowUser &&
                                                        <TableCell>{product.unitPriceWithVAT1 && product.unitPriceWithVAT1.toFixed(2)} RON</TableCell>}
                                                    {atacFlowUser && <TableCell>
                                                        <div className="flex-col">
                                                            <div className="">{product.multiplier}+ <span
                                                                className="text-red-500 ml-3">{product.unitPrice1 ? product.unitPrice1.toFixed(2) : "-"} RON</span>
                                                            </div>
                                                            {product.thresholdPrice2 &&
                                                                <div className="">{product.thresholdQuantity2}+ <span
                                                                    className="text-red-600 ml-3">{product.unitPrice2 ? product.unitPrice2.toFixed(2): "-"} RON</span>
                                                                </div>}
                                                            {product.thresholdPrice3 &&
                                                                <div className="">{product.thresholdQuantity3}+ <span
                                                                    className="text-red-700 ml-3">{product.unitPrice3 ? product.unitPrice3.toFixed(2) : "-"} RON</span>
                                                                </div>}
                                                        </div>
                                                    </TableCell>}
                                                    {atacFlowUser && <TableCell>
                                                        <div className="flex-col">
                                                            <div className="">{product.multiplier}+ <span
                                                                className="text-red-500 ml-3">{product.unitPriceWithVAT1 ? product.unitPriceWithVAT1.toFixed(2) : "-"} RON</span>
                                                            </div>
                                                            {product.thresholdPrice2 &&
                                                                <div className="">{product.thresholdQuantity2}+ <span
                                                                    className="text-red-600 ml-3">{product.unitPriceWithVAT2 ? product.unitPriceWithVAT2.toFixed(2) : "-"} RON</span>
                                                                </div>}
                                                            {product.thresholdPrice3 &&
                                                                <div className="">{product.thresholdQuantity3}+ <span
                                                                    className="text-red-700 ml-3">{product.unitPriceWithVAT3 ? product.unitPriceWithVAT3.toFixed(2) : "-"} RON</span>
                                                                </div>}
                                                        </div>
                                                    </TableCell>}
                                                    {/*<TableCell>{product.unitPriceWithVAT} RON</TableCell>*/}
                                                    {/* <TableCell>{product.taxArticles && product.taxArticles.tax?.activeTaxPrice ? product.taxArticles.tax.activeTaxPrice?.taxPriceValue : '-'} RON</TableCell> */}
                                                    <TableCell className="text-center">
                                                        <div className="text-center">
                                                            <div key={product.id}>
                                                                <ProductTable
                                                                    productData={product}
                                                                    key={product.id}
                                                                    horziontal={true}
                                                                />
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </table>
                            </div>
                        </div>
                    </section>
                )}

                {/*{isHorizontally && (*/}
                {/*    <section>*/}
                {/*        <div className="mx-auto max-w-screen-xl px-0 py-0 sm:px-6 sm:py-2 lg:px-8">*/}
                {/*            {data.items.map((article: ArticleModel) => {*/}
                {/*                let product = mapArticleModelToProductData(article);*/}
                {/*                return (*/}
                {/*                    <div key={product.id}>*/}
                {/*                        <ProductCard*/}
                {/*                            productData={product}*/}
                {/*                            key={product.id}*/}
                {/*                            autoPlayCarousel={false}*/}
                {/*                            //horziontal={true}*/}
                {/*                        />*/}
                {/*                    </div>*/}
                {/*                );*/}
                {/*            })}*/}
                {/*        </div>*/}
                {/*    </section>*/}
                {/*)}*/}

                <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                    <li>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 0) handlePageChange(currentPage - 1);
                            }}
                            className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                            aria-label="Previous page"
                            disabled={currentPage === 0}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </li>

                    <li>
                        <label htmlFor="Page" className="flex items-center gap-1">
                            <span className="sr-only">Pagina</span>
                            <input
                                type="number"
                                id="Page"
                                value={inputValue}
                                min={1}
                                max={data.totalPages}
                                onChange={handleInputChange}
                                className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                            />
                            <span className="text-sm">/ {data.totalPages}</span>
                        </label>
                    </li>

                    <li>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                            }}
                            className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                            aria-label="Next page"
                            disabled={currentPage === data.totalPages - 1}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="size-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </li>
                </ul>

            </>
            }
        </>
    );
}

export default ProductList;
